/**
 * FirmEconomicsSnapshot - the money, for whoever answers for it.
 *
 * The only dashboard card that fetches for itself. Everything else on the page
 * renders from the single `/dashboard/summary` slice, and that is worth
 * keeping: one request behind the page is why it paints at once. But firm
 * economics is not in that payload, it is licensed separately, and it is
 * relevant to a minority of users (org admins) - putting it in the summary
 * would make every member pay for a query none of them can read. So the widget
 * owns its own request, and only mounts for the persona that asked for it.
 *
 * TWO THINGS IT LEARNED from being looked at.
 *
 *   • THE WINDOW IS A SETTING, NOT A CAPTION. "Last 30 days" was a hard-coded
 *     constant printed as the card's SUBTITLE, which reads as a description of
 *     the card rather than as something you can change - and there was nothing
 *     to change it with. It is a `Badge` beside the title now, opened by
 *     `RangeControl`, and the chosen period really drives the request: the
 *     endpoint has taken `date_from`/`date_to` all along.
 *   • THE MIDDLE WAS EMPTY. Four KPI tiles across a full-width card is one
 *     short band with a lot of glass under it. The tiles fill the card height
 *     (`auto-rows-fr`), and the space they used to leave is now the top matters
 *     the revenue actually came from - which is the question anyone reading the
 *     revenue figure asks next.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PiArrowRightLight,
  PiBriefcaseLight,
  PiChartLineUpLight,
  PiClockLight,
  PiCoinsLight,
  PiPercentLight,
} from 'react-icons/pi';
import GlassCard from '../../shared/brand/GlassCard';
import { KpiTile } from '../../shared/brand/Field';
import Button from '../../shared/TailwindComponents/Button';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import { fetchFirmEconomics } from '../../../store/actions/economicsActions';
import { FirmEconomics } from '../../../types/economics';
import formatCurrency from '../../../utils/formatCurrency';
import logger from '../../../utils/logger';
import RangeControl, {
  DEFAULT_RANGE_KEY,
  DashboardRange,
  resolveRange,
} from './dashboardRange';

/**
 * The revenue line, whatever currency the org keeps.
 *
 * `totals.revenue` is null when more than one currency is in play - the
 * backend refuses to add them up, and so do we: the largest currency is shown
 * with a count of the rest rather than a meaningless sum.
 */
const revenueLine = (
  totals: FirmEconomics['totals'],
  t: (k: string, o?: any) => any
): { value: string; hint?: string } => {
  if (!totals) return { value: '—' };
  if (typeof totals.revenue === 'number') {
    const currency = totals.by_currency?.[0]?.currency;
    return { value: formatCurrency(totals.revenue, currency || 'usd') };
  }
  const spread = [...(totals.by_currency || [])].sort(
    (a, b) => (b.revenue || 0) - (a.revenue || 0)
  );
  if (spread.length === 0) return { value: '—' };
  const [top, ...rest] = spread;
  return {
    value: formatCurrency(top.revenue || 0, top.currency),
    hint: rest.length
      ? String(t('+{{count}} more currencies', { count: rest.length }))
      : undefined,
  };
};

const FirmEconomicsSnapshot: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [range, setRange] = useState<DashboardRange>(() => resolveRange(DEFAULT_RANGE_KEY));
  const [data, setData] = useState<FirmEconomics | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // The fetch outlives a fast unmount (edit mode remounts rows as they repack),
  // so a late response must not set state on a dead component.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const result = await fetchFirmEconomics({
        date_from: range.from,
        ...(range.to ? { date_to: range.to } : {}),
      })();
      if (alive.current) setData(result);
    } catch (error: any) {
      logger.error('[DASHBOARD] Firm economics snapshot failed', {
        error: error?.message || 'request failed',
      });
      if (alive.current) setFailed(true);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals;
  const revenue = revenueLine(totals, t);
  const hours = totals?.total_hours ?? 0;
  const billable = totals?.billable_hours ?? 0;
  const billablePct = hours > 0 ? Math.round((billable / hours) * 100) : 0;

  /** The five biggest matters in the window - what the revenue is made of. */
  const topMatters = useMemo(
    () =>
      [...(data?.top_matters || [])]
        .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
        .slice(0, 5),
    [data]
  );

  /*
    The period control and the way out are the same in every state of this
    card, including the failed one: a filter that disappears when the request
    it drives fails is a filter you cannot use to recover.
  */
  const actions = (
    <>
      <RangeControl
        value={range}
        onChange={setRange}
        label={t('Choose the period')}
        data-testid="firm-economics-range"
      />
      <Button
        variant="outline"
        size="small"
        onClick={() => history.push('/economics')}
        icon={<PiArrowRightLight className="h-4 w-4 rtl:-scale-x-100" />}
      >
        {t('Open')}
      </Button>
    </>
  );

  if (loading) {
    return (
      <GlassCard
        icon={<PiChartLineUpLight />}
        title={t('Firm Economics')}
        actions={actions}
        padding="none"
        className="overflow-hidden"
        data-testid="firm-economics"
      >
        <Skeleton variant="kpi" />
      </GlassCard>
    );
  }

  if (failed || !totals) {
    return (
      <GlassCard
        icon={<PiChartLineUpLight />}
        title={t('Firm Economics')}
        actions={actions}
        padding="none"
        className="overflow-hidden"
        data-testid="firm-economics"
      >
        <EmptyState
          density="compact"
          icon={<PiCoinsLight />}
          headline={failed ? t('Economics unavailable') : t('No billable activity yet')}
          subtext={
            failed
              ? t('We could not load the firm’s figures just now.')
              : t('Nothing was logged in this period — try a wider range.')
          }
          button={
            failed
              ? { label: t('Retry'), onClick: load }
              : { label: t('Open economics'), onClick: () => history.push('/economics') }
          }
        />
      </GlassCard>
    );
  }

  return (
    <GlassCard
      icon={<PiChartLineUpLight />}
      title={t('Firm Economics')}
      actions={actions}
      padding="none"
      className="overflow-hidden"
      fill
      bodyClassName="flex min-h-0 flex-1 flex-col"
      data-testid="firm-economics"
    >
      {/* Gaps rather than dividers - see QuickMetrics. A 2x2 block now, not a
          4-across band: the card is half a row (UI feedback D-5), so four tiles
          in a line would crush; two columns keeps each figure readable and the
          top-matters list carries the height below. */}
      <div className="grid flex-shrink-0 grid-cols-2 gap-1 p-1">
        {/* One standard for every dashboard headline number: text-2xl
            semibold, neutral ink. The figure is a fact, not a status — colour
            on the number is what made the dashboard look like confetti. Any
            real status rides the hint via hintTone. */}
        <KpiTile
          icon={<PiCoinsLight />}
          valueClassName="text-2xl font-medium tracking-tight"
          value={revenue.value}
          label={t('Revenue')}
          hint={revenue.hint || t('Recorded in the period')}
        />
        <KpiTile
          icon={<PiClockLight />}
          valueClassName="text-2xl font-medium tracking-tight"
          value={hours.toLocaleString()}
          label={t('Hours logged')}
          hint={t('{{count}} billable', { count: Math.round(billable) })}
        />
        <KpiTile
          icon={<PiPercentLight />}
          valueClassName="text-2xl font-medium tracking-tight"
          value={`${billablePct}%`}
          label={t('Billable share')}
          hint={t('Of all logged hours')}
          /* No tone here: the hint is descriptive ("of all logged hours"), not
             a status sentence, so colouring it green just reads as a random
             green line. Colour a hint only when the hint text itself names the
             status — as "14 overdue" does. */
        />
        <KpiTile
          icon={<PiBriefcaseLight />}
          valueClassName="text-2xl font-medium tracking-tight"
          value={(totals.matter_count ?? 0).toLocaleString()}
          label={t('Matters billed')}
          hint={t('With activity in the period')}
        />
      </div>

      {/*
        What the space under the strip is for. A four-tile band in a full-width
        card left a dead half; the matters the revenue came from is the next
        question anyone asks of that revenue figure, and it fills the card
        honestly rather than padding it.
      */}
      <div className="custom-scrollbar-main flex min-h-0 flex-1 flex-col divide-y divide-hairline-soft overflow-y-auto border-t border-hairline-soft [&>*]:flex-shrink-0">
        {topMatters.length === 0 ? (
          <p className="px-5 py-4 text-xs text-fg-muted">
            {t('No per-matter breakdown for this period.')}
          </p>
        ) : (
          topMatters.map((matter) => (
            <button
              key={matter.matter_uuid}
              type="button"
              onClick={() => history.push(`/matters/${matter.matter_uuid}`)}
              className="flex items-center gap-3 px-5 py-2.5 text-start transition-colors duration-200 ease-slide hover:bg-surface-sunken/70 focus:bg-surface-sunken/70 focus:outline-none"
            >
              <PiBriefcaseLight aria-hidden className="h-4 w-4 flex-shrink-0 text-fg-muted" />
              <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-200">
                {matter.matter_title || t('Untitled matter')}
              </span>
              <span className="flex-shrink-0 text-xs tabular-nums text-fg-muted">
                {t('{{count}}h', { count: Math.round(matter.hours || 0) })}
              </span>
              <span className="flex-shrink-0 text-sm tabular-nums text-gray-800 dark:text-gray-100">
                {formatCurrency(matter.revenue || 0, matter.currency || 'usd')}
              </span>
            </button>
          ))
        )}
      </div>
    </GlassCard>
  );
};

export default FirmEconomicsSnapshot;
